import type { Coding } from '@medplum/fhirtypes';

export const HCPCS_SYSTEM = 'http://terminology.hl7.org/CodeSystem/HCPCS';

export const HCPCS_CPAP_CODE = 'E0601';
export const HCPCS_BIPAP_CODE = 'E0470';

export const HCPCS_CPAP_CODING: Coding = {
  system: HCPCS_SYSTEM,
  code: HCPCS_CPAP_CODE,
  display: 'CPAP device',
};

export const HCPCS_BIPAP_CODING: Coding = {
  system: HCPCS_SYSTEM,
  code: HCPCS_BIPAP_CODE,
  display: 'BiPAP device',
};

export const DME_PRESSURE_EXTENSION_URL = 'https://polarissleep.com/fhir/StructureDefinition/dme-pressure-settings';
export const DME_PRESSURE_VALUE_URL = 'pressure';
export const DME_PRESSURE_IPAP_URL = 'ipap';
export const DME_PRESSURE_EPAP_URL = 'epap';

export const DME_MASK_EXTENSION_URL = 'https://polarissleep.com/fhir/StructureDefinition/dme-mask-type';

export const DME_MASK_TYPES = ['nasal', 'full-face', 'nasal-pillow', 'hybrid'] as const;
export type DmeMaskType = (typeof DME_MASK_TYPES)[number];

export const DME_MASK_TYPE_LABELS: Record<DmeMaskType, string> = {
  nasal: 'Nasal',
  'full-face': 'Full face',
  'nasal-pillow': 'Nasal pillow',
  hybrid: 'Hybrid',
};

export type DmeTherapyType = 'cpap' | 'bipap';

export const DME_THERAPY_TO_HCPCS: Record<DmeTherapyType, Coding> = {
  cpap: HCPCS_CPAP_CODING,
  bipap: HCPCS_BIPAP_CODING,
};

export const DME_THERAPY_LABELS: Record<DmeTherapyType, string> = {
  cpap: 'CPAP',
  bipap: 'BiPAP',
};

export const DME_TASK_CODE_SYSTEM = 'http://polarissleep.com/task-types';
export const SUBMIT_DME_ORDER_TASK_CODE = 'submit-dme-order';
export const SUBMIT_DME_ORDER_TASK_DISPLAY = 'Submit DME order';
export const DME_TASK_BUSINESS_STATUS_URL = 'https://polarissleep.com/fhir/StructureDefinition/task-business-status';
export const DME_TASK_VENDOR_URL = 'https://polarissleep.com/fhir/StructureDefinition/dme-vendor';

export const DME_TASK_BUSINESS_STATUSES = {
  new: 'New',
  pendingDocumentation: 'Pending documentation',
  claimed: 'Claimed',
  submitted: 'Submitted to vendor',
  acknowledged: 'Confirmed by vendor',
  blocked: 'Needs clinician input',
  closed: 'Closed',
} as const;

/** SLA due date: STAT same day, urgent 24h, routine two business days. */
export function getDmeTaskDueDate(priority: string | undefined, from = new Date()): string {
  const due = new Date(from);
  if (priority === 'stat') {
    due.setHours(23, 59, 59, 999);
  } else if (priority === 'urgent' || priority === 'asap') {
    due.setTime(due.getTime() + 24 * 60 * 60 * 1000);
  } else {
    let businessDays = 0;
    while (businessDays < 2) {
      due.setDate(due.getDate() + 1);
      const day = due.getDay();
      if (day !== 0 && day !== 6) {
        businessDays += 1;
      }
    }
    due.setHours(23, 59, 59, 999);
  }
  return due.toISOString();
}

export function isDmeTherapyType(value: string | undefined): value is DmeTherapyType {
  return value === 'cpap' || value === 'bipap';
}

export function isDmeMaskType(value: string | undefined): value is DmeMaskType {
  return DME_MASK_TYPES.includes(value as DmeMaskType);
}
